import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Button, Link, Typography, makeStyles } from '@material-ui/core';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import AddIcon from '@material-ui/icons/Add';
import { BOARD_TYPE } from '../../constants';
import { DESTINATION_KIND } from './aiPlacement';

const useStyles = makeStyles((theme) => ({
    // The action buttons sit side by side, tall, with the icon above the label so a wrapped
    // label still reads as one button.
    row: {
        display: 'flex',
        alignItems: 'stretch',
        '& > *': {
            marginLeft: theme.spacing(1),
        },
        '& > *:first-child': {
            marginLeft: 0,
        },
    },
    action: {
        flex: 1,
        minWidth: 0,
        minHeight: 76,
        textTransform: 'none',
        textAlign: 'center',
        fontSize: '0.8rem',
        fontWeight: 600,
        lineHeight: 1.25,
        padding: theme.spacing(1, 0.75),
        '& .MuiButton-label': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
        },
    },
    actionIcon: {
        marginBottom: theme.spacing(0.5),
    },
    hint: {
        display: 'block',
        marginTop: theme.spacing(0.5),
        color: theme.palette.text.secondary,
        fontSize: '0.72rem',
        lineHeight: 1.3,
    },
    caption: {
        marginTop: theme.spacing(0.75),
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
    },
    prefLabel: {
        marginBottom: theme.spacing(0.5),
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    prefGroup: {
        display: 'flex',
        width: '100%',
        marginBottom: theme.spacing(1.5),
    },
    prefButton: {
        flex: 1,
        textTransform: 'none',
        fontSize: '0.78rem',
        padding: theme.spacing(0.5, 1),
        color: theme.palette.text.primary,
        '&.Mui-selected': {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
        },
        '&.Mui-selected:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
}));

// One button per place the content can go; pressing one performs the add right away.
// `onAct(target)` receives the same destination shape placeAiResponse understands.
const AIDestinationActions = ({
    boardType,
    onAct,
    mainPagesUsed,
    maxMainPages,
    openedStudent,
    students,
    canMultiple,
    maxMultiPages,
    newPageForStudents,
    onNewPageForStudentsChange,
    onUpsell,
    disabled,
}) => {
    const classes = useStyles();
    const act = (target) => () => {
        if (onAct) onAct(target);
    };

    if (boardType === BOARD_TYPE.SINGLE_BOARD) {
        const pageLimitReached = mainPagesUsed >= maxMainPages;
        return (
            <Box>
                <div className={classes.row}>
                    <Button
                        variant='contained'
                        color='primary'
                        className={classes.action}
                        disabled={disabled || pageLimitReached}
                        onClick={act({ kind: DESTINATION_KIND.MAIN, newPage: true })}
                    >
                        <AddIcon className={classes.actionIcon} />
                        <FormattedMessage id='ai.action.newPage' />
                    </Button>
                    <Button
                        variant='outlined'
                        color='primary'
                        className={classes.action}
                        disabled={disabled}
                        onClick={act({ kind: DESTINATION_KIND.MAIN, newPage: false })}
                    >
                        <AddIcon className={classes.actionIcon} />
                        <FormattedMessage id='ai.action.currentPage' />
                    </Button>
                </div>
                {pageLimitReached ? (
                    <Typography component='span' className={classes.hint}>
                        <FormattedMessage id='ai.destination.pageLimitReached' />
                    </Typography>
                ) : null}
                <Typography className={classes.caption}>
                    <FormattedMessage id='ai.destination.pagesUsed' values={{ used: mainPagesUsed, max: maxMainPages }} />
                </Typography>
            </Box>
        );
    }

    const studentCount = students ? students.length : 0;
    const emails = (students || []).map((s) => s.email);
    const openedStudentEntry = openedStudent ? (students || []).find((s) => s.email === openedStudent) : null;
    const openedStudentName = openedStudentEntry ? openedStudentEntry.name : openedStudent;
    const newPage = canMultiple && newPageForStudents;
    const studentsAtLimit = (students || []).filter((s) => s.pages >= maxMultiPages).length;

    return (
        <Box>
            <Typography className={classes.prefLabel}>
                <FormattedMessage id='ai.destination.pageChoice' />
            </Typography>
            <ToggleButtonGroup
                exclusive
                size='small'
                className={classes.prefGroup}
                value={newPage ? 'new' : 'current'}
                onChange={(event, next) => {
                    if (next && onNewPageForStudentsChange) onNewPageForStudentsChange(next === 'new');
                }}
            >
                <ToggleButton value='new' className={classes.prefButton} disabled={disabled || !canMultiple}>
                    <FormattedMessage id='ai.destination.newPage' />
                </ToggleButton>
                <ToggleButton value='current' className={classes.prefButton} disabled={disabled}>
                    <FormattedMessage id='ai.destination.currentPage' />
                </ToggleButton>
            </ToggleButtonGroup>
            {!canMultiple ? (
                <Typography component='span' className={classes.hint} style={{ marginTop: -8, marginBottom: 12 }}>
                    <Link
                        component='button'
                        type='button'
                        variant='caption'
                        onClick={(e) => {
                            e.preventDefault();
                            if (onUpsell) onUpsell();
                        }}
                    >
                        <FormattedMessage id='ai.destination.upgrade' />
                    </Link>
                </Typography>
            ) : null}

            <div className={classes.row}>
                <Button
                    variant='contained'
                    color='primary'
                    className={classes.action}
                    disabled={disabled || !openedStudent}
                    onClick={act({ kind: DESTINATION_KIND.STUDENT, email: openedStudent, newPage })}
                >
                    <AddIcon className={classes.actionIcon} />
                    {openedStudent ? (
                        <FormattedMessage id='ai.action.student' values={{ name: openedStudentName }} />
                    ) : (
                        <FormattedMessage id='ai.action.studentNone' />
                    )}
                </Button>
                <Button
                    variant='outlined'
                    color='primary'
                    className={classes.action}
                    disabled={disabled || studentCount === 0}
                    onClick={act({ kind: DESTINATION_KIND.STUDENTS, emails, newPage, variants: false })}
                >
                    <AddIcon className={classes.actionIcon} />
                    <FormattedMessage id='ai.action.allSame' values={{ count: studentCount }} />
                </Button>
                <Button
                    variant='outlined'
                    color='primary'
                    className={classes.action}
                    disabled={disabled || studentCount === 0}
                    onClick={act({ kind: DESTINATION_KIND.STUDENTS, emails, newPage, variants: true })}
                >
                    <AddIcon className={classes.actionIcon} />
                    <FormattedMessage id='ai.action.allVariants' values={{ count: studentCount }} />
                </Button>
            </div>
            {!openedStudent ? (
                <Typography component='span' className={classes.hint}>
                    <FormattedMessage id='ai.destination.noOpenedStudent' />
                </Typography>
            ) : null}
            {studentCount === 0 ? (
                <Typography component='span' className={classes.hint}>
                    <FormattedMessage id='ai.destination.noStudents' />
                </Typography>
            ) : null}
            {newPage && studentsAtLimit > 0 ? (
                <Typography component='span' className={classes.hint}>
                    <FormattedMessage
                        id='ai.destination.studentsAtLimit'
                        values={{ count: studentsAtLimit, max: maxMultiPages }}
                    />
                </Typography>
            ) : null}
        </Box>
    );
};

export default AIDestinationActions;
